import { graphql, useStaticQuery } from 'gatsby';
import i18next from 'i18next';
import React from 'react';

import { SuperBlocks } from '../../../../config/certification-settings';
import envData from '../../../../config/env.json';
import { isAuditedCert } from '../../../../utils/is-audited';
import { generateIconComponent } from '../../assets/icons';
import LinkButton from '../../assets/icons/link-button';
import { ChallengeNode } from '../../redux/prop-types';
import { Link, Spacer } from '../helpers';

import './map.css';

const { curriculumLocale } = envData;

interface MapProps {
  currentSuperBlock?: SuperBlocks | null;
  forLanding?: boolean;
  userAccessLevel: number;
}

interface MapData {
  allChallengeNode: {
    nodes: ChallengeNode[];
  };
}

function createSuperBlockTitle(superBlock: SuperBlocks) {
  const superBlockTitle = i18next.t(`intro:${superBlock}.title`);
  return superBlock === 'coding-interview-prep'
    ? i18next.t('learn.cert-map-estimates.coding-prep', {
        title: superBlockTitle
      })
    : i18next.t('learn.cert-map-estimates.certs', { title: superBlockTitle });
}

const linkSpacingStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};

function renderLandingMap(nodes: ChallengeNode[],
  userAccessLevel: MapProps['userAccessLevel']
) {
  // console.log('userAccessLevel', userAccessLevel)
  nodes = nodes.filter(
    ({ challenge }) => challenge.superBlock !== SuperBlocks.CodingInterviewPrep
  );
  return (
    <ul data-test-label='certifications'>
      {nodes.map(({ challenge }, i) => (
        <li key={i}>
          <Link
            className='btn link-btn btn-lg'
            to={`/learn/${challenge.superBlock}/`}
          >
            <div style={linkSpacingStyle}>
              {generateIconComponent(challenge.superBlock, 'map-icon')}
              {i18next.t(`intro:${challenge.superBlock}.title`)}
            </div>
            <LinkButton />
          </Link>
        </li>
      ))}
    </ul>
  );
}

function renderLearnMap(
  nodes: ChallengeNode[],
  currentSuperBlock: MapProps['currentSuperBlock'],
  userAccessLevel: MapProps['userAccessLevel']
) {
  console.log('userAccessLevel', userAccessLevel)
  nodes = nodes.filter(
    ({ challenge }) => challenge.superBlock !== currentSuperBlock
  );
  return curriculumLocale === 'english' ? (
    <ul data-test-label='learn-curriculum-map'>
      {nodes.map(({ challenge }, i) => (
       <li
       key={i}
       className={`course_${i}_${userAccessLevel}`}
       style={(() => {
         console.log(challenge);
         if (userAccessLevel === 1) {
           return i > 6 ? { display: 'none' } : { display: 'block' }; // Tier 1: 7 certs (i 0-6)
         } else if (userAccessLevel === 2) {
           return i > 4 ? { display: 'none' } : { display: 'block' }; // Tier 2: 5 certs (i 0-4)
         } else if (userAccessLevel === 3) {
          return i > 3 ? { display: 'none' } : { display: 'block' }; // Hide i > 6 and i === 0
        }
         else {
           return i > 1 ? { display: 'none' } : { display: 'block' }; // Hide i > 6 and i === 0
         }
       })()}
     >
          <Link
            className='btn link-btn btn-lg'
            to={`/learn/${challenge.superBlock}?tier=${userAccessLevel}`}
          >
            <div style={linkSpacingStyle}>
              {generateIconComponent(challenge.superBlock, 'map-icon')}
              {createSuperBlockTitle(challenge.superBlock)}
            </div>
          </Link>
        </li>
      ))}
    </ul>
  ) : (
    <ul data-test-label='learn-curriculum-map'>
      {nodes
        .filter(({ challenge }) =>
          isAuditedCert(curriculumLocale, challenge.superBlock)
        )
        .map(({ challenge }, i) => (
          <li key={i}>
            <Link
              className='btn link-btn btn-lg'
              to={`/learn/${challenge.superBlock}/`}
            >
              <div style={linkSpacingStyle}>
                {generateIconComponent(challenge.superBlock, 'map-icon')}
                {createSuperBlockTitle(challenge.superBlock)}
              </div>
            </Link>
          </li>
        ))}
      <hr />
      <div style={{ textAlign: 'center' }}>
        <p style={{ marginBottom: 0 }}>{i18next.t('learn.help-translate')} </p>
        <Link
          external={true}
          sameTab={false}
          to={i18next.t('links:help-translate-link-url')}
        >
          {i18next.t('learn.help-translate-link')}
        </Link>
        <Spacer />
      </div>
      {nodes
        .filter(
          ({ challenge }) =>
            !isAuditedCert(curriculumLocale, challenge.superBlock)
        )
        .map(({ challenge }, i) => (
          <li key={i}>
            <Link
              className='btn link-btn btn-lg'
              to={`/learn/${challenge.superBlock}?tier=${userAccessLevel}`}
            >
              <div style={linkSpacingStyle}>
                {generateIconComponent(challenge.superBlock, 'map-icon')}
                {createSuperBlockTitle(challenge.superBlock)}
              </div>
            </Link>
          </li>
        ))}
    </ul>
  );
}

export function Map({
  forLanding = false,
  currentSuperBlock = null,
  userAccessLevel
}: MapProps): React.ReactElement {
  /*
   * this query gets the first challenge from each block and the first block
   * from each superblock, leaving you with one challenge from each
   * superblock
   */
  const data: MapData = useStaticQuery(graphql`
    query SuperBlockNodes {
      allChallengeNode(
        sort: { fields: [challenge___superOrder] }
        filter: { challenge: { order: { eq: 0 }, challengeOrder: { eq: 0 } } }
      ) {
        nodes {
          challenge {
            superBlock
            dashedName
          }
        }
      }
    }
  `);

  const nodes = data.allChallengeNode.nodes;

  return (
    <div className='map-ui' data-test-label='learn-curriculum-map'>
      {forLanding
        ? renderLandingMap(nodes, userAccessLevel)
        : renderLearnMap(nodes, currentSuperBlock, userAccessLevel)}
    </div>
  );
}

Map.displayName = 'Map';

export default Map;
